/**
 * Modal de création rapide d'un contrat de sous-traitance
 * Utilisé depuis le modal coûts annexes pour créer un contrat ST à la volée
 */

import React, { useState } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import { z } from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useContratsST } from '@/hooks/useContratsST';
import type { ContratSousTraitance, ContratSTCreate } from '@/types/sous-traitant';
import { useToast } from '@/hooks/use-toast';

// Schéma de validation
const contratSchema = z.object({
  montant_contrat_ht: z.number().positive('Le montant doit être supérieur à 0'),
  description_travaux: z.string().max(500, '500 caractères max').optional(),
});

interface ContratSTQuickCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chantierId: string;
  sousTraitantId: string;
  onSuccess: (newContrat: ContratSousTraitance) => void;
}

export default function ContratSTQuickCreate({
  open,
  onOpenChange,
  chantierId,
  sousTraitantId,
  onSuccess,
}: ContratSTQuickCreateProps) {
  const { toast } = useToast();
  useModalProtection(open);
  const { createContratST } = useContratsST(chantierId);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    montant_contrat_ht: 0,
    taux_tva: 20,
    description_travaux: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const result = contratSchema.safeParse(formData);
    if (!result.success) {
      const firstError = result.error.errors[0];
      toast({
        title: 'Données invalides',
        description: firstError.message,
        variant: 'destructive',
      });
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      const dataToCreate: ContratSTCreate = {
        chantier_id: chantierId,
        sous_traitant_id: sousTraitantId,
        montant_contrat_ht: formData.montant_contrat_ht,
        taux_tva: formData.taux_tva,
        description_travaux: formData.description_travaux.trim() || null,
        statut: 'en_cours',
      };

      const newContrat = await createContratST(dataToCreate);

      if (newContrat) {
        // Reset form
        setFormData({
          montant_contrat_ht: 0,
          taux_tva: 20,
          description_travaux: '',
        });
        onSuccess(newContrat);
      }
    } catch (error) {
      console.error('[ContratSTQuickCreate] Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        montant_contrat_ht: 0,
        description_travaux: '',
      });
      onOpenChange(false);
    }
  };

  // Formater en devise
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau contrat de sous-traitance</DialogTitle>
          <DialogDescription>
            Définissez le montant prévu pour ce sous-traitant sur ce chantier
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Montant du contrat (requis) */}
          <div className="space-y-2">
            <Label htmlFor="contrat-montant">
              Montant du contrat HT <span className="text-destructive">*</span>
            </Label>
            <div className="grid grid-cols-[1fr_100px] gap-3">
              <Input
                id="contrat-montant"
                type="number"
                step="0.01"
                min="0"
                value={formData.montant_contrat_ht || ''}
                onChange={(e) =>
                  setFormData({ ...formData, montant_contrat_ht: parseFloat(e.target.value) || 0 })
                }
                placeholder="Ex: 15000"
                required
              />
              <Select
                value={formData.taux_tva.toString()}
                onValueChange={(v) => setFormData({ ...formData, taux_tva: parseFloat(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">TVA 0%</SelectItem>
                  <SelectItem value="5.5">TVA 5,5%</SelectItem>
                  <SelectItem value="10">TVA 10%</SelectItem>
                  <SelectItem value="20">TVA 20%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.montant_contrat_ht > 0 && (
              <p className="text-sm text-muted-foreground">
                {formatCurrency(formData.montant_contrat_ht)} HT → {formatCurrency(formData.montant_contrat_ht * (1 + formData.taux_tva / 100))} TTC
              </p>
            )}
          </div>

          {/* Description des travaux */}
          <div className="space-y-2">
            <Label htmlFor="contrat-description">Description des travaux</Label>
            <Textarea
              id="contrat-description"
              value={formData.description_travaux}
              onChange={(e) =>
                setFormData({ ...formData, description_travaux: e.target.value })
              }
              placeholder="Ex: Installation électrique complète RDC et étage"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading || formData.montant_contrat_ht <= 0}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le contrat
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
